import { type ReactNode } from 'react';
import { Badge, Text } from '@mantine/core';
import Countdown from 'react-countdown';
import dayjs from 'dayjs';
import {
    formatOrder,
    payoutOrderPriorityColors,
    type PayoutTraderOrderStatus,
    payoutTraderOrderStatusColors,
    type PayoutOrderPriority,
    type PayoutOrderStatus,
    payoutOrderStatusColors,
    type PayoutOrderPublicStatus,
    payoutOrderPublicStatusColors,
} from '~/entities/payout';

export const createOrderPriorityBadge = (priority?: PayoutOrderPriority, fullWidth = false): ReactNode => {
    if (!priority) {
        return null;
    }

    return (
        <Badge
            variant='dot'
            fullWidth={fullWidth}
            color={payoutOrderPriorityColors[priority]}
        >
            {formatOrder.priority(priority)}
        </Badge>
    );
};

const statusColors = { ...payoutTraderOrderStatusColors, ...payoutOrderStatusColors };

export const createOrderStatusBadge = (
    status?: PayoutTraderOrderStatus | PayoutOrderStatus | PayoutOrderPublicStatus,
    fullWidth = false,
): ReactNode => {
    const isPublicStatus = payoutOrderPublicStatusColors[status as PayoutOrderPublicStatus];

    if (!status) {
        return null;
    }

    return (
        <Badge
            variant='dot'
            fullWidth={fullWidth}
            color={
                isPublicStatus
                    ? payoutOrderPublicStatusColors[status as PayoutOrderPublicStatus]
                    : statusColors[status as PayoutTraderOrderStatus | PayoutOrderStatus]
            }
        >
            {isPublicStatus
                ? formatOrder.publicStatus(status as PayoutOrderPublicStatus)
                : formatOrder.status(status as PayoutTraderOrderStatus | PayoutOrderStatus)}
        </Badge>
    );
};

const formatTime = (days: number, hours: number, minutes: number, seconds: number) => {
    const totalHours = days * 24 + hours;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${totalHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const createOrderCountdown = (expiresAt?: string) => {
    if (!expiresAt) {
        return '';
    }

    const expiresAtDate = dayjs(expiresAt);

    if (!expiresAtDate.isValid()) {
        return '';
    }

    const now = dayjs();

    if (expiresAtDate.isBefore(now) || expiresAtDate.isSame(now)) {
        return <Text>-</Text>;
    }

    return (
        <Countdown
            date={expiresAt}
            renderer={({ completed, days, hours, minutes, seconds }) => {
                if (completed) {
                    return <Text>-</Text>;
                }

                return formatTime(days, hours, minutes, seconds);
            }}
        />
    );
};
